import { zodResolver } from "@hookform/resolvers/zod";
import { ExternalLink, Eye, Plus, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { impersonationApi } from "@/client/features/admin/impersonation-api";
import { type AdminTenant, tenantsAdminApi } from "@/client/features/admin/tenants-admin-api";
import { TenantStatusBadge } from "@/client/features/admin/tenant-status-badge";
import { ApiError } from "@/client/lib/api";

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  slug: z
    .string()
    .trim()
    .min(1, "Slug is required")
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Lowercase letters, numbers, and hyphens"),
  status: z.enum(["active", "suspended"]),
  slackChannelId: z
    .string()
    .trim()
    .max(50)
    .refine((v) => v === "" || /^[A-Z0-9]+$/i.test(v), "Enter a valid Slack channel id."),
  slackInternalChannelId: z
    .string()
    .trim()
    .max(50)
    .refine((v) => v === "" || /^[A-Z0-9]+$/i.test(v), "Enter a valid Slack channel id."),
});

type FormValues = z.infer<typeof formSchema>;

/** Open a Slack channel by id in the user's Slack (workspace-agnostic redirect). */
function slackChannelUrl(channelId: string): string {
  return `https://slack.com/app_redirect?channel=${channelId}`;
}

function TenantFormDialog({
  mode,
  tenant,
  trigger,
  onSaved,
}: {
  mode: "create" | "edit";
  tenant?: AdminTenant;
  trigger: React.ReactNode;
  onSaved: (t: AdminTenant) => void;
}) {
  const [open, setOpen] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      slug: "",
      status: "active",
      slackChannelId: "",
      slackInternalChannelId: "",
    },
  });

  // Reset to the row's values only when the dialog opens, so an edit in
  // progress isn't wiped by a parent re-render (e.g. list refresh).
  useEffect(() => {
    if (open) {
      form.reset({
        name: tenant?.name ?? "",
        slug: tenant?.slug ?? "",
        status: (tenant?.status as "active" | "suspended") ?? "active",
        slackChannelId: tenant?.slackChannelId ?? "",
        slackInternalChannelId: tenant?.slackInternalChannelId ?? "",
      });
    }
  }, [open, tenant, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      const saved =
        mode === "create"
          ? await tenantsAdminApi.create(values)
          : await tenantsAdminApi.update(tenant!.id, values);
      if (mode === "create" && saved.provisioningStatus === "failed") {
        // The tenant row was created, but the NetBox/Key Vault provisioning
        // failed. Surface it loudly; the row shows a Retry action.
        toast.error(
          `Tenant created, but provisioning failed: ${saved.provisioningError ?? "unknown error"}. Use Retry on the row.`,
        );
      } else {
        toast.success(mode === "create" ? "Tenant created." : "Tenant updated.");
      }
      onSaved(saved);
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the tenant.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New tenant" : "Edit tenant"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="VAST Data" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input placeholder="vast" className="font-mono" spellCheck={false} {...field} />
                  </FormControl>
                  <FormDescription>Lowercase identifier used internally.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <select
                      className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none"
                      {...field}
                    >
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="slackChannelId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Joint Slack channel</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="C0B2XKTKMU3"
                      className="font-mono"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Channel shared with the customer. Mantle-to-customer announcements (maintenance,
                    published docs) post here. Leave blank to skip these for this tenant.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="slackInternalChannelId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Internal Slack channel</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="C0B2XKTKMU3"
                      className="font-mono"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Mantle-only channel. Customer-initiated activity (support tickets, holds, team
                    changes, SSH keys) posts here. Leave blank to skip these for this tenant.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={form.formState.isSubmitting}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting
                  ? "Saving…"
                  : mode === "create"
                    ? "Create tenant"
                    : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Opens the customer portal as this tenant in a read-only staff view. It's a
 * full navigation (not an in-app route): the server hands off to the customer
 * host and sets the impersonation cookie there.
 */
function ViewAsButton({ tenant }: { tenant: AdminTenant }) {
  const [loading, setLoading] = useState(false);

  // "View as" is a full navigation away, so we never reset `loading` on the way
  // out. When the staffer comes back via the browser's back/forward cache the
  // page is restored with `loading` still true, freezing the button on
  // "Opening…". Reset it on bfcache restore (pageshow with persisted).
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLoading(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const onClick = async () => {
    setLoading(true);
    try {
      const { url } = await impersonationApi.start(tenant.id);
      window.location.assign(url);
    } catch (err) {
      setLoading(false);
      toast.error(err instanceof ApiError ? err.message : "Couldn't open the tenant view.");
    }
  };
  return (
    <Button variant="ghost" size="sm" onClick={onClick} disabled={loading}>
      <Eye className="size-4" />
      <span className="grid">
        <span className="col-start-1 row-start-1 opacity-0" aria-hidden="true">
          Opening…
        </span>
        <span className="col-start-1 row-start-1">{loading ? "Opening…" : "View as"}</span>
      </span>
    </Button>
  );
}

/**
 * The tenant's NetBox/Key Vault provisioning state (PE-980). A `failed` tenant
 * shows the error on hover and, for admins, a Retry that re-runs the idempotent
 * orchestrator and updates the row in place.
 */
function ProvisioningCell({
  tenant,
  canEdit,
  onSaved,
}: {
  tenant: AdminTenant;
  canEdit: boolean;
  onSaved: (t: AdminTenant) => void;
}) {
  const [retrying, setRetrying] = useState(false);

  if (tenant.provisioningStatus === "provisioned") {
    return <Badge variant="secondary">Provisioned</Badge>;
  }

  const onRetry = async () => {
    setRetrying(true);
    try {
      const updated = await tenantsAdminApi.retryProvision(tenant.id);
      onSaved(updated);
      if (updated.provisioningStatus === "provisioned") {
        toast.success("Tenant provisioned.");
      } else {
        toast.error(updated.provisioningError ?? "Provisioning failed again.");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't retry provisioning.");
    } finally {
      setRetrying(false);
    }
  };

  // `pending` (mid-provision, or stuck after a crash) and `failed` both offer a
  // Retry: POST /tenants/:id/provision is idempotent and re-runnable either way.
  const isPending = tenant.provisioningStatus === "pending";
  return (
    <div className="flex items-center gap-2">
      <Badge
        variant={isPending ? "outline" : "destructive"}
        title={tenant.provisioningError ?? undefined}
      >
        {isPending ? "Pending" : "Failed"}
      </Badge>
      {canEdit ? (
        <Button variant="ghost" size="sm" onClick={onRetry} disabled={retrying}>
          <RefreshCw className="size-4" />
          {retrying ? "Retrying…" : "Retry"}
        </Button>
      ) : null}
    </div>
  );
}

export function StaffTenantsPage() {
  const canEdit = useAuthStore((s) => s.user?.staffRole === "admin");
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [error, setError] = useState(false);

  const refresh = async () => {
    try {
      const res = await tenantsAdminApi.list();
      setTenants(res.tenants);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load tenants.");
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const onSaved = (saved: AdminTenant) => {
    setTenants((prev) => {
      if (!prev) return [saved];
      const exists = prev.some((t) => t.id === saved.id);
      const next = exists ? prev.map((t) => (t.id === saved.id ? saved : t)) : [...prev, saved];
      return next.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Tenants" description="Customer organizations on the platform.">
        {canEdit ? (
          <TenantFormDialog
            mode="create"
            trigger={
              <Button size="sm">
                <Plus className="size-4" />
                New tenant
              </Button>
            }
            onSaved={onSaved}
          />
        ) : null}
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Organizations</CardTitle>
          <CardDescription>
            Open a tenant to see its members and SSH keys, or edit its details and Slack mapping.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && tenants === null ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load tenants. Refresh to try again.
            </p>
          ) : tenants === null ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : tenants.length === 0 ? (
            <p className="text-muted-foreground text-sm">No tenants yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Slug</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Provisioning</th>
                    <th className="py-2 pr-4 font-medium">Joint channel</th>
                    <th className="py-2 pr-4 font-medium">Internal channel</th>
                    {canEdit ? <th className="py-2 text-right font-medium">Actions</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">
                        <Link to={`/admin/tenants/${t.id}`} className="hover:underline">
                          {t.name}
                        </Link>
                      </td>
                      <td className="py-2 pr-4">
                        <a
                          href={t.netboxUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open in NetBox"
                          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-mono text-xs hover:underline"
                        >
                          {t.slug}
                          <ExternalLink className="size-3" />
                        </a>
                      </td>
                      <td className="py-2 pr-4">
                        <TenantStatusBadge status={t.status} deactivatedAt={t.deactivatedAt} />
                      </td>
                      <td className="py-2 pr-4">
                        <ProvisioningCell tenant={t} canEdit={canEdit} onSaved={onSaved} />
                      </td>
                      <td className="py-2 pr-4">
                        {t.slackChannelId ? (
                          <a
                            href={slackChannelUrl(t.slackChannelId)}
                            target="_blank"
                            rel="noreferrer"
                            title="Open in Slack"
                            className="font-mono text-xs hover:underline"
                          >
                            {t.slackChannelId}
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">Not set</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {t.slackInternalChannelId ? (
                          <a
                            href={slackChannelUrl(t.slackInternalChannelId)}
                            target="_blank"
                            rel="noreferrer"
                            title="Open in Slack"
                            className="font-mono text-xs hover:underline"
                          >
                            {t.slackInternalChannelId}
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">Not set</span>
                        )}
                      </td>
                      {canEdit ? (
                        <td className="py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <ViewAsButton tenant={t} />
                            <TenantFormDialog
                              mode="edit"
                              tenant={t}
                              trigger={
                                <Button variant="ghost" size="sm">
                                  Edit
                                </Button>
                              }
                              onSaved={onSaved}
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
